/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import {
  expectSome,
  getAccountMetaFactory,
  type ResolvedAccount,
} from "../shared";
import {
  getCredentialKindDecoder,
  getCredentialKindEncoder,
  type CredentialKind,
  type CredentialKindArgs,
} from "../types";

export const CREATE_CREDENTIAL_TYPE_DISCRIMINATOR = new Uint8Array([
  211, 208, 67, 175, 20, 248, 8, 227,
]);

export function getCreateCredentialTypeDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    CREATE_CREDENTIAL_TYPE_DISCRIMINATOR,
  );
}

export type CreateCredentialTypeInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountCredentialType extends string | AccountMeta<string> = string,
  TAccountAuthority extends string | AccountMeta<string> = string,
  TAccountSystemProgram extends string | AccountMeta<string> =
    "11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountCredentialType extends string
        ? WritableAccount<TAccountCredentialType>
        : TAccountCredentialType,
      TAccountAuthority extends string
        ? WritableSignerAccount<TAccountAuthority> &
            AccountSignerMeta<TAccountAuthority>
        : TAccountAuthority,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export type CreateCredentialTypeInstructionData = {
  discriminator: ReadonlyUint8Array;
  name: string;
  kind: CredentialKind;
  schemaUri: string;
  description: string;
};

export type CreateCredentialTypeInstructionDataArgs = {
  name: string;
  kind: CredentialKindArgs;
  schemaUri: string;
  description: string;
};

export function getCreateCredentialTypeInstructionDataEncoder(): Encoder<CreateCredentialTypeInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["name", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["kind", getCredentialKindEncoder()],
      ["schemaUri", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["description", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
    ]),
    (value) => ({
      ...value,
      discriminator: CREATE_CREDENTIAL_TYPE_DISCRIMINATOR,
    }),
  );
}

export function getCreateCredentialTypeInstructionDataDecoder(): Decoder<CreateCredentialTypeInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["name", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["kind", getCredentialKindDecoder()],
    ["schemaUri", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["description", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
  ]);
}

export function getCreateCredentialTypeInstructionDataCodec(): Codec<
  CreateCredentialTypeInstructionDataArgs,
  CreateCredentialTypeInstructionData
> {
  return combineCodec(
    getCreateCredentialTypeInstructionDataEncoder(),
    getCreateCredentialTypeInstructionDataDecoder(),
  );
}

export type CreateCredentialTypeAsyncInput<
  TAccountCredentialType extends string = string,
  TAccountAuthority extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  credentialType?: Address<TAccountCredentialType>;
  authority: TransactionSigner<TAccountAuthority>;
  systemProgram?: Address<TAccountSystemProgram>;
  name: CreateCredentialTypeInstructionDataArgs["name"];
  kind: CreateCredentialTypeInstructionDataArgs["kind"];
  schemaUri: CreateCredentialTypeInstructionDataArgs["schemaUri"];
  description: CreateCredentialTypeInstructionDataArgs["description"];
};

export async function getCreateCredentialTypeInstructionAsync<
  TAccountCredentialType extends string,
  TAccountAuthority extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: CreateCredentialTypeAsyncInput<
    TAccountCredentialType,
    TAccountAuthority,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  CreateCredentialTypeInstruction<
    TProgramAddress,
    TAccountCredentialType,
    TAccountAuthority,
    TAccountSystemProgram
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    credentialType: { value: input.credentialType ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.credentialType.value) {
    accounts.credentialType.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            99, 114, 101, 100, 101, 110, 116, 105, 97, 108, 95, 116, 121, 112,
            101,
          ]),
        ),
        getUtf8Encoder().encode(expectSome(args.name)),
      ],
    });
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.credentialType),
      getAccountMeta(accounts.authority),
      getAccountMeta(accounts.systemProgram),
    ],
    data: getCreateCredentialTypeInstructionDataEncoder().encode(
      args as CreateCredentialTypeInstructionDataArgs,
    ),
    programAddress,
  } as CreateCredentialTypeInstruction<
    TProgramAddress,
    TAccountCredentialType,
    TAccountAuthority,
    TAccountSystemProgram
  >);
}

export type CreateCredentialTypeInput<
  TAccountCredentialType extends string = string,
  TAccountAuthority extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  credentialType: Address<TAccountCredentialType>;
  authority: TransactionSigner<TAccountAuthority>;
  systemProgram?: Address<TAccountSystemProgram>;
  name: CreateCredentialTypeInstructionDataArgs["name"];
  kind: CreateCredentialTypeInstructionDataArgs["kind"];
  schemaUri: CreateCredentialTypeInstructionDataArgs["schemaUri"];
  description: CreateCredentialTypeInstructionDataArgs["description"];
};

export function getCreateCredentialTypeInstruction<
  TAccountCredentialType extends string,
  TAccountAuthority extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: CreateCredentialTypeInput<
    TAccountCredentialType,
    TAccountAuthority,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): CreateCredentialTypeInstruction<
  TProgramAddress,
  TAccountCredentialType,
  TAccountAuthority,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    credentialType: { value: input.credentialType ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.credentialType),
      getAccountMeta(accounts.authority),
      getAccountMeta(accounts.systemProgram),
    ],
    data: getCreateCredentialTypeInstructionDataEncoder().encode(
      args as CreateCredentialTypeInstructionDataArgs,
    ),
    programAddress,
  } as CreateCredentialTypeInstruction<
    TProgramAddress,
    TAccountCredentialType,
    TAccountAuthority,
    TAccountSystemProgram
  >);
}

export type ParsedCreateCredentialTypeInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    credentialType: TAccountMetas[0];
    authority: TAccountMetas[1];
    systemProgram: TAccountMetas[2];
  };
  data: CreateCredentialTypeInstructionData;
};

export function parseCreateCredentialTypeInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedCreateCredentialTypeInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 3) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      credentialType: getNextAccount(),
      authority: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getCreateCredentialTypeInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
